// @ts-strict
import { sanitizeUrl } from "@braintree/sanitize-url";
import { isEmpty } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  useLocation,
  useNavigate,
  Location,
  useParams,
} from "react-router-dom";
import { Routes } from "../../observability/routes";

import AccountLocked from "@/components/containers/AccountLocked";
import AccountUnlock from "@/components/containers/AccountUnlock";
import Logout from "@/components/containers/Logout";
import PasswordReset from "@/components/containers/PasswordReset";
import ReplRedirect from "@/components/containers/ReplRedirect";
import RequireTwoFactor from "@/components/containers/RequireTwoFactor";
import LogOutErrorModal from "@/components/modals/LogOutErrorModal";
import { PageTitle } from "@/components/shared/PageTitle";
import MultipleTeamsError from "@/components/static/MultipleTeamsError";
import NotFound from "@/components/static/NotFound";
import PrivacyPolicy from "@/components/static/PrivacyPolicy";
import TermsOfService from "@/components/static/TermsOfService";
import { notifyError, VendorUtilities } from "@/utilities/VendorUtilities";

import NavBar from "./NavBar";
import { CompromisedPasswordAlert } from "./CompromisedPasswordAlert";
import RedirectChangelog from "./RedirectChangelog";
import { shouldShowCompromisedPasswordBanner } from "./compromisedPasswordBanner";

import {
  AsyncAccountSettings,
  AsyncApps,
  AsyncAppsTroubleshootContainer,
  AsyncCLILogin,
  AsyncCreateApp,
  AsyncSupport,
  AsyncTeamSettingsComponent,
  AsyncTroubleshootContainer,
} from "./AsyncMainComponents";

import AuthGoogleError from "@/components/auth/AuthGoogleError";
import CompleteSignup from "@/components/auth/CompleteSignup";
import VerifyEmail from "@/components/auth/VerifyEmail";
import AcceptInvite from "@/components/containers/AcceptInvite";
import AuthGoogleComplete from "@/components/containers/AuthGoogleComplete";
import CreateTeam from "@/components/containers/CreateTeam";
import Login from "../containers/Login";
import SystemLogin from "@/components/auth/SystemLogin";
import Signup from "@/components/containers/Signup";
import TeamSelection from "@/components/containers/TeamSelection";
import VerifySignup from "@/components/containers/VerifySignup";
import { useAppState } from "@/context/app-state";
import { useKotsInstallerAppFlag } from "@/hooks/use-flag";
import useLegacyBreakpoint from "@/hooks/use-legacy-breakpoint";
import useSendEvent from "@/hooks/use-send-event";
import {
  useDismissPwnedBannerMutation,
  useFeatureValues,
  useLogOutUserMutation,
  usePlatformAppsQuery,
  useTeam,
  useTeamEntitlements,
} from "@/query";
import {
  AsyncCompatibilityMatrix,
  AsyncNotifications,
  AsyncNotificationAIForm,
  AsyncNotificationForm,
  AsyncNotificationEdit,
  AsyncNotificationHistory,
} from "../apps/AsyncComponents";
import SAML from "../auth/SAML";
import SAMLLogin from "../auth/SAMLLogin";
import { User, BreakpointType } from "@/types";

const useAnalytics = ({ enabled }: { enabled: boolean }) => {
  const location = useLocation();
  const lastLocation = useRef<typeof location | null>(null);

  const sendEvent = useSendEvent();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (location.pathname !== lastLocation.current?.pathname) {
      sendEvent("page_view");
    } else if (location.search !== lastLocation.current?.search) {
      sendEvent("params_change");
    } else if (location.hash !== lastLocation.current?.hash) {
      sendEvent("hash_change");
    }
    lastLocation.current = location;
  }, [location]);
  return null;
};

// Wrapper component that provides router props
const ReplRedirectWrapper = ({ nextRoute }: { nextRoute: string }) => {
  // Even though ReplRedirect is wrapped with withRouting, we still need to provide
  // router props due to how Redux's connect affects type inference
  return <ReplRedirect nextRoute={nextRoute} />;
};

interface AppsContainerProps {
  user: User;
  location: Location;
  breakpoint: BreakpointType;
}

const AppsContainer = ({ user, location, breakpoint }: AppsContainerProps) => (
  <AsyncAppsTroubleshootContainer user={user}>
    <AsyncApps breakpoint={breakpoint} user={user} location={location} />
  </AsyncAppsTroubleshootContainer>
);

// Redirect components for legacy bundle routes
const RedirectToAnalyze = () => {
  const { id } = useParams();
  return <Navigate to={`/troubleshoot/analyze/${id}`} replace />;
};

const RedirectToAnalyzeContents = () => {
  const { id } = useParams();
  return <Navigate to={`/troubleshoot/analyze/${id}/contents`} replace />;
};

const Main = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [
    hasProcessedLegacyStaticRedirects,
    setHasProcessedLegacyStaticRedirects,
  ] = useState(false);
  const [hasProcessedLegacySlug, setHasProcessedLegacySlug] = useState(false);
  const breakpoint = useLegacyBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();

  const isKotsInstallerApp = useKotsInstallerAppFlag();
  const {
    accessToken,
    currentAppId,
    isAuthenticated,
    isTroubleshootOpenedFromNavbar,
    setIsTroubleshootOpenedFromNavbar,
    user,
  } = useAppState();
  const { features } = useFeatureValues();

  const team = useTeam();
  const teamEntitlements = useTeamEntitlements();
  const hasADP = teamEntitlements?.application_distribution_platform !== false;
  const { data: platformAppsData, isSuccess: platformAppsQueryIsSuccess } =
    usePlatformAppsQuery(hasADP && !isEmpty(teamEntitlements));

  const platformApps = platformAppsData?.platformApps;
  const platformAppsList = platformAppsData?.platformAppsList;

  const { mutate: logOutUser } = useLogOutUserMutation();
  const { mutate: dismissCompromisedPasswordBanner } =
    useDismissPwnedBannerMutation();

  const cmxEnabled = teamEntitlements?.compatibility_matrix;

  useEffect(() => {
    if (!hasADP) {
      if (
        location.pathname === "/new-application" ||
        location.pathname.startsWith("/app") ||
        location.pathname.startsWith("/apps") ||
        location.pathname.startsWith("/notifications")
      ) {
        navigate("/", { replace: true });
      }
    }
  }, [hasADP, location.pathname, navigate]);

  useAnalytics({ enabled: Boolean(user?.id && accessToken && team?.id) });

  const getNextPath = () => {
    const nextPath = sanitizeUrl(location.hash.slice(1));
    const isValid = VendorUtilities.validReplicatedUrl(nextPath);
    if (isValid) {
      return nextPath;
    }
    return "";
  };

  const shouldProcessUrlWithHash = Boolean(
    location.hash &&
    !location.hash.includes("#L") &&
    location.pathname.indexOf("troubleshoot") !== -1
  );

  // This is gross but it's supporting legacy code from original VenWeb
  const processLegacyStaticRedirects = () => {
    const nextPath = getNextPath();
    if (
      !nextPath.includes("/account") &&
      !nextPath.includes("/password-reset")
    ) {
      navigate(nextPath);
      return;
    }
    if (!nextPath.includes("/account")) {
      return;
    }
    if (!accessToken) {
      logOutUser(true);
      return;
    }
    if (
      ["/select-team", "/create-team", "/invite"].includes(location.pathname)
    ) {
      return;
    }
    setHasProcessedLegacyStaticRedirects(true);
  };

  const processLegacySlug = () => {
    if (isEmpty(platformApps) || !platformAppsList) {
      navigate("/new-application");
      return;
    }
    const platformSlug = platformApps[platformAppsList[0]]?.Slug;
    if (!platformSlug) {
      return;
    }
    const nextPath = getNextPath();
    navigate(`/apps/${platformSlug}${nextPath}`);
  };

  useEffect(() => {
    if (!shouldProcessUrlWithHash) {
      VendorUtilities.purgeVOneData();
    }
  }, [shouldProcessUrlWithHash]);

  useEffect(() => {
    if (
      hasProcessedLegacyStaticRedirects &&
      !hasProcessedLegacySlug &&
      platformAppsQueryIsSuccess
    ) {
      setHasProcessedLegacySlug(true);
      processLegacySlug();
    }
  }, [
    platformAppsQueryIsSuccess,
    hasProcessedLegacyStaticRedirects,
    hasProcessedLegacySlug,
  ]);

  useEffect(() => {
    if (shouldProcessUrlWithHash) {
      processLegacyStaticRedirects();
    }
  }, [shouldProcessUrlWithHash]);

  useEffect(() => {
    setShowBanner(shouldShowCompromisedPasswordBanner(user));
  }, [user]);

  const handleCloseCompromisedPasswordBanner = () => {
    dismissCompromisedPasswordBanner(undefined, {
      onSuccess: () => setShowBanner(false),
      onError: (error) =>
        notifyError(error instanceof Error ? error : new Error(String(error)), {
          component: "main",
          action: "dismissCompromisedPasswordBanner",
        }),
    });
  };

  const isAuthPage =
    !accessToken || (accessToken && !user?.id) || location.pathname === "/saml";

  if (!isAuthPage && isEmpty(features)) {
    return null;
  }

  return (
    <div className="w-full overflow-hidden flex flex-col h-full">
      {!isAuthPage && (
        <>
          <NavBar
            hasLegacySchedulers={teamEntitlements?.native_scheduler}
            teamEntitlements={teamEntitlements}
            handleLogOut={logOutUser}
            user={user}
            breakpoint={breakpoint}
            isTroubleshootOpenedFromNavbar={isTroubleshootOpenedFromNavbar}
            setIsTroubleshootOpenFromNavbar={setIsTroubleshootOpenedFromNavbar}
            selectedApp={currentAppId}
            apps={platformApps}
            isKotsInstallerApp={isKotsInstallerApp}
          />
        </>
      )}
      <CompromisedPasswordAlert
        showBanner={showBanner}
        onCloseBanner={handleCloseCompromisedPasswordBanner}
      />
      <div className="h-full w-full flex flex-col flex-1 min-h-0 overflow-hidden">
        <Routes>
          <Route
            path="/crashz"
            element={(() => {
              const Crashz = () => {
                throw new Error("Crashz!");
              };
              return <Crashz />;
            })()}
          />

          <Route
            path="/"
            element={
              accessToken ? (
                !hasADP && cmxEnabled ? (
                  <Navigate to="/compatibility-matrix/overview" replace />
                ) : (
                  <AppsContainer
                    user={user}
                    location={location}
                    breakpoint={breakpoint}
                  />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/*"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <AppsContainer
                  user={user}
                  location={location}
                  breakpoint={breakpoint}
                />
              )
            }
          />

          <Route
            path="/apps/*"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <AppsContainer
                  user={user}
                  location={location}
                  breakpoint={breakpoint}
                />
              )
            }
          />

          <Route
            path="/login"
            element={
              <>
                <PageTitle>Log in to Replicated</PageTitle>
                {/* 
                    Even though Login is wrapped with withRouting HOC which injects these props at runtime,
                    we still need to pass them explicitly here because:
                    1. The base Login component is written in TypeScript with strict prop types
                    2. TypeScript can't understand that withRouting will inject these props
                  */}
                <Login
                  breakpoint={breakpoint}
                  navigate={navigate}
                  location={location}
                  params={{}}
                />
              </>

            }
          />

          <Route
            path="/replicated-system-user"
            element={
              <>
                <PageTitle>Log in to Replicated</PageTitle>
                <SystemLogin />
              </>
            }
          />

          <Route
            path="/login-saml"
            element={
              <>
                <PageTitle>SAML Login - Replicated</PageTitle>
                <SAMLLogin />
              </>
            }
          />

          <Route path="/auth/google/error" element={<AuthGoogleError />} />
          <Route
            path="/auth/google/complete"
            element={<AuthGoogleComplete />}
          />
          <Route path="/logout" element={<Logout />} />

          <Route
            path="/signup"
            element={
              <>
                <PageTitle>Sign up to Replicated</PageTitle>
                <Signup />
              </>
            }
          />

          <Route path="/signup-builders" element={<Navigate to="/signup" />} />

          <Route
            path="/signup-business"
            element={
              <>
                <PageTitle>Sign up to Replicated</PageTitle>
                <Signup />
              </>
            }
          />

          <Route
            path="/invite"
            element={
              <>
                <PageTitle>Accept Invite</PageTitle>
                <AcceptInvite />
              </>
            }
          />

          <Route
            path="/signup/verify"
            element={
              <>
                <PageTitle>Activate</PageTitle>
                <VerifySignup />
              </>
            }
          />

          <Route
            path="/verify-notification-email"
            element={
              <>
                <PageTitle>Verify Notification Email</PageTitle>
                <VerifyEmail />
              </>
            }
          />

          <Route
            path="/complete_signup"
            element={
              <>
                <PageTitle>Log in to Replicated</PageTitle>
                <CompleteSignup />
              </>
            }
          />

          <Route
            path="/create-team"
            element={
              <>
                <PageTitle>Create Team</PageTitle>
                <CreateTeam />
              </>
            }
          />

          <Route
            path="/select-team"
            element={
              <>
                <PageTitle>Select Team</PageTitle>
                <TeamSelection />
              </>
            }
          />

          <Route
            path="/saml"
            element={
              <>
                <PageTitle>Log in to Replicated</PageTitle>
                <SAML />
              </>
            }
          />

          <Route
            path="/saml/multiple_teams"
            element={<MultipleTeamsError breakpoint={breakpoint} />}
          />

          <Route path="/account/locked" element={<AccountLocked />} />
          <Route path="/account/unlock" element={<AccountUnlock />} />

          <Route
            path="/account/password-reset"
            element={
              <>
                <PageTitle>Reset Password</PageTitle>
                <PasswordReset />
              </>
            }
          />

          <Route
            path="/apps/:appSlug?/*"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <AppsContainer
                  user={user}
                  location={location}
                  breakpoint={breakpoint}
                />
              )
            }
          />

          <Route
            path="/new-application"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>New Application</PageTitle>
                  <AsyncCreateApp breakpoint={breakpoint} user={user} />
                </>
              )
            }
          />

          <Route
            path="/team/*"
            element={
              <>
                <PageTitle>Team</PageTitle>
                <AsyncTeamSettingsComponent
                  breakpoint={breakpoint}
                  location={location}
                  user={user}
                />
              </>
            }
          />

          <Route
            path="/troubleshoot/collectors"
            element={
              <ReplRedirectWrapper nextRoute="/troubleshoot/collectors" />
            }
          />

          <Route
            path="/troubleshoot/analyzers"
            element={
              <ReplRedirectWrapper nextRoute="/troubleshoot/analyzers" />
            }
          />

          <Route
            path="/troubleshoot/bundles/analyze/:id/contents/*"
            element={<RedirectToAnalyzeContents />}
          />

          <Route
            path="/troubleshoot/bundles/analyze/:id"
            element={<RedirectToAnalyze />}
          />

          <Route
            path="/troubleshoot/bundles"
            element={<Navigate to="/troubleshoot" replace />}
          />

          <Route
            path="/troubleshoot/*"
            element={
              <AsyncAppsTroubleshootContainer user={user}>
                <AsyncTroubleshootContainer
                  breakpoint={breakpoint}
                  user={user}
                  team={team}
                  teamEntitlements={teamEntitlements}
                />
              </AsyncAppsTroubleshootContainer>
            }
          />

          <Route
            path="/compatibility-matrix/*"
            element={
              <>
                <PageTitle>Compatibility Matrix</PageTitle>
                <AsyncCompatibilityMatrix />
              </>
            }
          />

          <Route
            path="/notifications/new"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>Create Notification</PageTitle>
                  <AsyncNotificationForm />
                </>
              )
            }
          />

          <Route
            path="/notifications/new/ai"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>AI Notification Builder</PageTitle>
                  <AsyncNotificationAIForm />
                </>
              )
            }
          />

          <Route
            path="/notifications/:notificationId/history"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>Notification History</PageTitle>
                  <AsyncNotificationHistory />
                </>
              )
            }
          />

          <Route
            path="/notifications/:id/edit"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>Edit Notification</PageTitle>
                  <AsyncNotificationEdit />
                </>
              )
            }
          />

          <Route
            path="/notifications/my-notifications"
            element={<Navigate to="/notifications" replace />}
          />

          <Route
            path="/notifications"
            element={
              !hasADP ? (
                <Navigate to="/" replace />
              ) : (
                <>
                  <PageTitle>Notifications</PageTitle>
                  <AsyncNotifications />
                </>
              )
            }
          />

          <Route
            path="/changelog"
            element={
              <>
                <PageTitle>Changelog</PageTitle>
                <RedirectChangelog />
              </>
            }
          />

          <Route
            path="/releases"
            element={<ReplRedirectWrapper nextRoute="/releases" />}
          />

          <Route
            path="/channels"
            element={<ReplRedirectWrapper nextRoute="/channels" />}
          />

          <Route
            path="/licenses"
            element={<ReplRedirectWrapper nextRoute="/customers" />}
          />

          <Route
            path="/customers"
            element={<ReplRedirectWrapper nextRoute="/customers" />}
          />

          <Route
            path="/license-fields"
            element={<ReplRedirectWrapper nextRoute="/license-fields" />}
          />

          <Route
            path="/images"
            element={<ReplRedirectWrapper nextRoute="/images" />}
          />

          <Route
            path="/kurl"
            element={<ReplRedirectWrapper nextRoute="/kurl" />}
          />

          <Route
            path="/settings"
            element={<ReplRedirectWrapper nextRoute="/settings" />}
          />

          <Route
            path="/support"
            element={
              <>
                {!isAuthenticated && (
                  <Navigate
                    to={`/login?next=${encodeURIComponent(
                      `/support${location.search}`
                    )}`}
                    replace
                  />
                )}
                {isAuthenticated && (
                  <AsyncAppsTroubleshootContainer user={user}>
                    <PageTitle>Support</PageTitle>
                    <AsyncSupport breakpoint={breakpoint} />
                  </AsyncAppsTroubleshootContainer>
                )}
              </>
            }
          />

          <Route
            path="/require-2fa"
            element={<RequireTwoFactor breakpoint={breakpoint} />}
          />

          <Route
            path="/cli-login"
            element={
              <>
                <PageTitle>CLI Login</PageTitle>
                <AsyncCLILogin />
              </>
            }
          />

          <Route
            path="/account-settings/*"
            element={
              <>
                <PageTitle>Account Settings</PageTitle>
                <AsyncAccountSettings breakpoint={breakpoint} />
              </>
            }
          />

          <Route
            path="/privacy"
            element={
              <>
                <PageTitle>Privacy Policy</PageTitle>
                <PrivacyPolicy />
              </>
            }
          />

          <Route
            path="/terms"
            element={
              <>
                <PageTitle>Terms of Service</PageTitle>
                <TermsOfService />
              </>
            }
          />

          <Route
            path="/audit-log"
            element={<Navigate to="/team/audit-log" />}
          />

          <Route
            path="*"
            element={
              <>
                <PageTitle>404</PageTitle>
                <NotFound />
              </>
            }
          />
        </Routes>
      </div>
      <LogOutErrorModal />
    </div>
  );
};
export default Main;
